import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Modal, Spinner } from 'react-bootstrap';

import {
  AdminTestingToolsAvailableQuery,
  FillUserPlaylistsXpAdminMutation,
  FillUserPlaylistsXpAdminMutationVariables,
} from '../../../../../../__gqltypes__';
import { ADMIN_TESTING_TOOLS_AVAILABLE, FILL_USER_PLAYLISTS_XP_ADMIN } from '../../../graphql';

/**
 * TEST only (hidden elsewhere, refused by the server on beta / prod): every playlist / artist of the
 * player gets its XP full up to the next level, the level kept, so an upgrade can be tested right away.
 */
export default function FillPlaylistsXpButton({ userId }: { userId: string }) {
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const { data } = useQuery<AdminTestingToolsAvailableQuery>(ADMIN_TESTING_TOOLS_AVAILABLE);
  const [fill, { loading, error }] = useMutation<
    FillUserPlaylistsXpAdminMutation,
    FillUserPlaylistsXpAdminMutationVariables
  >(FILL_USER_PLAYLISTS_XP_ADMIN, {
    refetchQueries: ['searchUser'],
    onCompleted: ({ fillUserPlaylistsXpAdmin: result }) =>
      setNotice(`${result.filled} filled, ${result.alreadyFull} already full, ${result.maxLevel} at the max level.`),
  });

  if (!data?.adminTestingToolsAvailable) {
    return null;
  }
  return (
    <>
      <Button
        variant="outline-warning"
        size="sm"
        disabled={loading}
        title="TEST only: XP full on every playlist, level kept (no upgrade)"
        onClick={(event: React.MouseEvent) => {
          // inside the accordion header: do not toggle it
          event.stopPropagation();
          setNotice(null);
          setConfirming(true);
        }}
      >
        {loading ? <Spinner animation="border" size="sm" /> : 'Fill XP (all playlists)'}
      </Button>
      {(notice || error) && (
        <small className={`ml-2 ${error ? 'text-danger' : 'text-muted'}`}>{error?.message ?? notice}</small>
      )}

      <Modal show={confirming} onHide={() => setConfirming(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Fill the XP of every playlist?</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-muted">
          Every playlist and artist gets its XP full up to the next level. The level does not change: the upgrade is
          done in game.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={() => {
              setConfirming(false);
              fill({ variables: { userId } }).catch(() => {
                // surfaced through error
              });
            }}
          >
            Fill XP
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
